import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Database, FolderKanban, BarChart3, ArrowRight } from 'lucide-react';

export const SolutionSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const pillars = [
    {
      icon: Database,
      title: 'Master Directory',
      subtitle: '500+ Curated Backlink Sources. Always Up-to-Date.',
      description:
        'Browse a vetted directory of high-authority sites across every category — Guest Posts, Web 2.0s, Forums, Business Directories, Social Bookmarks, Article Submissions, and more. Filter by Domain Authority, Page Authority, Spam Score, DoFollow/NoFollow type, and submission type (Free/Paid). Every site is manually vetted. Bad links removed regularly. New ones added continuously.',
      color: '#3B82F6',
    },
    {
      icon: FolderKanban,
      title: 'Unlimited Project Tracker',
      subtitle: 'Track Every Link from Outreach to Live Publication.',
      description:
        'Create unlimited projects, each tied to a target URL and anchor text. Add backlink sources from the directory or add your own custom links. Track status per link: Not Started → Submitted → Published or Rejected. See your success rate per project. Zero chaos.',
      color: '#8B5CF6',
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      subtitle: 'Real-Time Visibility Across All Your Campaigns.',
      description:
        'Your dashboard shows total published links, unique referring domains, project success rates, and a visual status breakdown. See at a glance what is working and what needs follow-up.',
      color: '#EC4899',
    },
  ];

  return (
    <section
      id="features"
      data-testid="solution-section"
      className="section-light py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            Everything You Need to Build Links{' '}
            <span className="gradient-text">Smarter, Not Harder</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Three powerful pillars that transform your link-building workflow from chaotic to systematic.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="relative p-8 rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50/50 hover:shadow-xl transition-all duration-300 group"
              data-testid={`pillar-card-${index}`}
            >
              {/* Icon */}
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6"
                style={{ backgroundColor: `${pillar.color}15` }}
              >
                <pillar.icon size={28} style={{ color: pillar.color }} />
              </div>

              {/* Content */}
              <h3 className="font-display text-xl font-bold text-gray-900 mb-2">
                {pillar.title}
              </h3>
              <p
                className="text-sm font-medium mb-4"
                style={{ color: pillar.color }}
              >
                {pillar.subtitle}
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                {pillar.description}
              </p>

              {/* Hover indicator */}
              <div className="mt-6 flex items-center gap-2 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: pillar.color }}>
                <span>Learn more</span>
                <ArrowRight size={16} />
              </div>

              {/* Decorative gradient border on hover */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                style={{
                  background: `linear-gradient(135deg, ${pillar.color}10, transparent)`,
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <a
            href="https://checkout.dodopayments.com/buy/pdt_0Nc1Sd7RjzAPh38CNHxE9?quantity=1"
            data-testid="solution-cta"
            className="btn-primary inline-flex items-center gap-2"
          >
            <span>Get Lifetime Access — $9</span>
            <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionSection;
